'use client'

import { ObjectDisplay } from '@/components/WorkflowToolResults/shared/ObjectDisplay'
import { cn } from '@/lib/utils'
import { getToolDisplay } from '@/utils/copilotToolMappings'
import { Bug, Check, Copy, X } from 'lucide-react'
import { useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

export function ToolChip({
  toolCall,
  isOpen,
  onToggle,
}: {
  toolCall: { function: { name: string; arguments: Record<string, unknown> } }
  isOpen: boolean
  onToggle: () => void
}) {
  const [copied, setCopied] = useState(false)
  const [debugMode, setDebugMode] = useState(false)
  const [popoverPosition, setPopoverPosition] = useState<{ top: number; left: number } | null>(null)
  const chipRef = useRef<HTMLButtonElement>(null)
  const { displayName, icon } = getToolDisplay(toolCall.function.name)

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation()
    await navigator.clipboard.writeText(JSON.stringify(toolCall.function, null, 2))
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  useLayoutEffect(() => {
    if (isOpen && chipRef.current) {
      const rect = chipRef.current.getBoundingClientRect()
      setPopoverPosition({
        top: rect.bottom + 8,
        left: rect.left,
      })
    } else {
      setPopoverPosition(null)
    }
  }, [isOpen])

  return (
    <div className="flex-shrink-0">
      <button
        ref={chipRef}
        onClick={onToggle}
        className="flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-gray-300 bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-200"
      >
        <span className="flex-shrink-0 text-gray-500 [&>svg]:h-3 [&>svg]:w-3">{icon}</span>
        {displayName}
      </button>

      {isOpen &&
        popoverPosition &&
        createPortal(
          <>
            {/* Backdrop for click-outside */}
            <div className="fixed inset-0 z-[10000]" onClick={onToggle} />

            {/* Popover content */}
            <div
              className="font-space-grotesk fixed z-[10001] w-[28rem] max-w-md rounded-lg border border-gray-200 bg-white p-3 shadow-lg"
              style={{ top: `${popoverPosition.top}px`, left: `${popoverPosition.left}px` }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="mb-2 flex items-center justify-between border-b border-gray-200 pb-2">
                <div className="flex items-center gap-2">
                  <span className="text-gray-600 [&>svg]:h-4 [&>svg]:w-4">{icon}</span>
                  <span className="text-sm font-medium text-gray-900">{displayName}</span>
                </div>
                <button onClick={onToggle} className="rounded p-1 hover:bg-gray-100">
                  <X className="h-4 w-4 text-gray-600" />
                </button>
              </div>

              {/* Arguments */}
              <div>
                <p className="mb-1 text-xs font-medium text-gray-700">Arguments</p>
                {debugMode ? (
                  <pre className="font-consolas max-h-64 overflow-auto rounded bg-gray-50 p-2 text-xs text-gray-600">
                    {JSON.stringify(toolCall.function.arguments, null, 2)}
                  </pre>
                ) : (
                  <div className="max-h-64 overflow-auto rounded bg-gray-50 p-2">
                    <ObjectDisplay data={toolCall.function.arguments} />
                  </div>
                )}
              </div>

              {/* Footer buttons */}
              <div className="mt-2 flex justify-between">
                <button
                  onClick={() => setDebugMode((v) => !v)}
                  className={cn(
                    'flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium transition-colors',
                    debugMode
                      ? 'bg-gray-200 text-gray-800'
                      : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                  )}
                  title={debugMode ? 'Hide raw JSON' : 'Show raw JSON (debug)'}
                >
                  <Bug className="h-3 w-3" />
                  {debugMode ? 'Debug on' : 'Debug'}
                </button>
                <button
                  onClick={handleCopy}
                  className="flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
                >
                  {copied ? (
                    <>
                      <Check className="h-3 w-3 text-green-600" />
                      <span className="text-green-600">Copied</span>
                    </>
                  ) : (
                    <>
                      <Copy className="h-3 w-3" />
                      <span>Copy JSON</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </>,
          document.body
        )}
    </div>
  )
}
